import { API_BASE_URL } from "../config/api";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";

const STATUS_OPTIONS = ["New", "Contacted", "Quotation Sent", "Closed"];

const formatDate = (value) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "-";

  return parsed.toLocaleString("en-IN", {
    dateStyle: "medium",
    timeStyle: "short"
  });
};

const badgeClass = (status) => {
  const normalized = String(status || "").toLowerCase().replace(/\s+/g, "-");
  return `inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.12em] status-${normalized}`;
};

export default function WebsiteEnquiries() {
  const navigate = useNavigate();
  const location = useLocation();
  const [enquiries, setEnquiries] = useState([]);
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [totalCount, setTotalCount] = useState(0);
  const [newCount, setNewCount] = useState(0);
  const [modalNotice, setModalNotice] = useState("");

  const loadSummary = useCallback(async () => {
    const res = await axios.get(API_BASE_URL + "/api/enquiries", {
      params: { status: "New", limit: 1 }
    });
    setNewCount(Number(res.data?.count || 0));
  }, []);

  const loadEnquiries = useCallback(async () => {
    setRefreshing(true);
    try {
      const params = new URLSearchParams({
        search: search.trim(),
        status: statusFilter,
        sortBy,
        order: sortOrder,
        limit: "1000"
      });

      const res = await axios.get(API_BASE_URL + `/api/enquiries?${params.toString()}`);
      const data = Array.isArray(res.data?.data) ? res.data.data : [];
      setEnquiries(data);
      setTotalCount(Number(res.data?.count || data.length || 0));
    } catch (error) {
      setEnquiries([]);
      setTotalCount(0);
    } finally {
      setRefreshing(false);
      setLoading(false);
    }
  }, [search, statusFilter, sortBy, sortOrder]);

  useEffect(() => {
    let timer = window.setTimeout(() => {
      loadEnquiries();
    }, search ? 220 : 0);

    return () => window.clearTimeout(timer);
  }, [loadEnquiries, search]);

  useEffect(() => {
    loadSummary().catch(() => setNewCount(0));
  }, [loadSummary]);

  const refreshAll = async () => {
    await Promise.allSettled([loadEnquiries(), loadSummary()]);
  };

  const openEnquiry = (enquiry) => {
    setSelectedEnquiry(enquiry);
    setModalNotice("");
  };

  const closeModal = () => {
    setSelectedEnquiry(null);
    setModalNotice("");
  };

  const updateStatus = async () => {
    if (!selectedEnquiry) return;

    try {
      const res = await axios.put(API_BASE_URL + `/api/enquiries/${selectedEnquiry._id}/status`, {
        status: selectedEnquiry.status
      });
      setSelectedEnquiry(res.data?.data || selectedEnquiry);
      setModalNotice("Status updated successfully.");
      await Promise.all([loadEnquiries(), loadSummary()]);
    } catch (error) {
      setModalNotice(error.response?.data?.message || error.message || "Failed to update status.");
    }
  };

  const deleteEnquiry = async () => {
    if (!selectedEnquiry) return;
    if (!window.confirm("Delete this enquiry permanently?")) return;

    try {
      await axios.delete(API_BASE_URL + `/api/enquiries/${selectedEnquiry._id}`);
      closeModal();
      await Promise.all([loadEnquiries(), loadSummary()]);
    } catch (error) {
      setModalNotice(error.response?.data?.message || error.message || "Failed to delete enquiry.");
    }
  };

  const generateQuotation = () => {
    if (!selectedEnquiry) return;

    navigate("/admin/quotations", {
      state: {
        quotationPrefill: {
          customerName: selectedEnquiry.company || selectedEnquiry.name || "",
          company: selectedEnquiry.company || "",
          contactPerson: selectedEnquiry.name || "",
          phone: selectedEnquiry.phone || "",
          email: selectedEnquiry.email || "",
          notes: selectedEnquiry.message || "",
          subject: selectedEnquiry.serviceRequired
            ? `Quotation for ${selectedEnquiry.serviceRequired}`
            : "Quotation for website enquiry",
          serviceRequired: selectedEnquiry.serviceRequired || "",
        },
        prefillKey: `website-enquiry-${selectedEnquiry._id}-${Date.now()}`,
        fromPath: location.pathname
      }
    });
  };

  const enquiryCountLabel = useMemo(() => {
    if (loading) return "Loading...";
    return `${totalCount} ${totalCount === 1 ? "enquiry" : "enquiries"}`;
  }, [loading, totalCount]);

  return (
    <div className="space-y-5 lg:space-y-8">
      <section className="overflow-hidden rounded-[24px] border border-rose-200 bg-[radial-gradient(circle_at_top_left,_rgba(244,63,94,0.18),_transparent_34%),linear-gradient(135deg,_#fff,_#fff7f8,_#ffe4e6)] p-4 shadow-sm lg:rounded-[2rem] lg:p-8">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-rose-700 lg:text-xs">Contacts</p>
            <h1 className="mt-2 text-2xl font-bold text-slate-950 lg:text-3xl">Website Enquiries</h1>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600 lg:text-base">
              Track public contact form submissions, update their status, and convert promising leads into quotations.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:min-w-[22rem]">
            <div className="rounded-2xl border border-white/70 bg-white/80 p-4 shadow-sm">
              <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">New</p>
              <p className="mt-2 text-3xl font-bold leading-none text-rose-700">{newCount}</p>
            </div>
            <div className="rounded-2xl border border-white/70 bg-white/80 p-4 shadow-sm">
              <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">Total</p>
              <p className="mt-2 text-3xl font-bold leading-none text-slate-950">{totalCount}</p>
              <p className="mt-1 text-xs text-slate-500">{enquiryCountLabel}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm lg:rounded-[2rem] lg:p-6">
        <div className="mb-4 flex flex-col gap-3 border-b border-slate-100 pb-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="text-lg font-bold text-slate-950 lg:text-xl">Enquiry Register</h2>
            <p className="text-sm text-slate-500">Search, filter, review, and assign next actions.</p>
          </div>
          <button
            type="button"
            onClick={refreshAll}
            className="rounded-xl bg-slate-950 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800"
          >
            {refreshing ? "Refreshing..." : "Refresh"}
          </button>
        </div>

        <div className="grid gap-3 md:grid-cols-[1.6fr,1fr,1fr,1fr]">
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search name, phone, email, company, message..."
            className="rounded-xl border border-slate-300 px-4 py-3 text-sm outline-none transition focus:border-rose-500 focus:ring-2 focus:ring-rose-100"
          />
          <select
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
            className="rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm outline-none transition focus:border-rose-500 focus:ring-2 focus:ring-rose-100"
          >
            <option value="">All statuses</option>
            {STATUS_OPTIONS.map((status) => (
              <option key={status} value={status}>{status}</option>
            ))}
          </select>
          <select
            value={sortBy}
            onChange={(event) => setSortBy(event.target.value)}
            className="rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm outline-none transition focus:border-rose-500 focus:ring-2 focus:ring-rose-100"
          >
            <option value="createdAt">Date created</option>
            <option value="updatedAt">Date updated</option>
            <option value="name">Customer name</option>
            <option value="status">Status</option>
          </select>
          <select
            value={sortOrder}
            onChange={(event) => setSortOrder(event.target.value)}
            className="rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm outline-none transition focus:border-rose-500 focus:ring-2 focus:ring-rose-100"
          >
            <option value="desc">Newest first</option>
            <option value="asc">Oldest first</option>
          </select>
        </div>

        <div className="mt-5 overflow-hidden rounded-2xl border border-slate-200">
          <div className="overflow-x-auto">
            <table className="min-w-[980px] w-full border-collapse text-left text-sm">
              <thead className="bg-slate-50">
                <tr className="text-[11px] uppercase tracking-[0.16em] text-slate-500">
                  <th className="px-4 py-3 font-semibold">Name</th>
                  <th className="px-4 py-3 font-semibold">Company</th>
                  <th className="px-4 py-3 font-semibold">Phone</th>
                  <th className="px-4 py-3 font-semibold">Service</th>
                  <th className="px-4 py-3 font-semibold">Status</th>
                  <th className="px-4 py-3 font-semibold">Created</th>
                  <th className="px-4 py-3 font-semibold">Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="7" className="px-4 py-10 text-center text-slate-400">Loading enquiries...</td>
                  </tr>
                ) : enquiries.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-4 py-10 text-center text-slate-400">No enquiries found.</td>
                  </tr>
                ) : enquiries.map((enquiry) => (
                  <tr key={enquiry._id} className="border-t border-slate-100 hover:bg-slate-50/70">
                    <td className="px-4 py-4 align-top">
                      <div className="font-semibold text-slate-950">{enquiry.name || "-"}</div>
                      <div className="text-xs text-slate-500">{enquiry.email || "-"}</div>
                    </td>
                    <td className="px-4 py-4 align-top text-slate-700">{enquiry.company || "-"}</td>
                    <td className="px-4 py-4 align-top text-slate-700">{enquiry.phone || "-"}</td>
                    <td className="px-4 py-4 align-top text-slate-700">{enquiry.serviceRequired || "-"}</td>
                    <td className="px-4 py-4 align-top">
                      <span className={badgeClass(enquiry.status)}>{enquiry.status || "New"}</span>
                    </td>
                    <td className="px-4 py-4 align-top text-slate-600">{formatDate(enquiry.createdAt)}</td>
                    <td className="px-4 py-4 align-top">
                      <button
                        type="button"
                        onClick={() => openEnquiry(enquiry)}
                        className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition hover:border-rose-300 hover:bg-rose-50 hover:text-rose-700"
                      >
                        Open
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {selectedEnquiry && (
        <div
          className="fixed inset-0 z-50 grid place-items-center bg-slate-950/60 p-4 backdrop-blur-sm"
          onClick={(event) => {
            if (event.target === event.currentTarget) {
              closeModal();
            }
          }}
        >
          <div className="w-full max-w-4xl overflow-hidden rounded-[2rem] border border-white/20 bg-white shadow-2xl">
            <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-4 lg:px-6">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-rose-700">Enquiry details</p>
                <h3 className="mt-1 text-xl font-bold text-slate-950">
                  {selectedEnquiry.name || "Website Enquiry"}
                </h3>
              </div>
              <button
                type="button"
                onClick={closeModal}
                className="rounded-xl border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
              >
                Close
              </button>
            </div>

            <div className="grid gap-4 px-5 py-5 lg:grid-cols-2 lg:px-6">
              <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
                <h4 className="mb-4 text-sm font-bold uppercase tracking-[0.16em] text-slate-500">Customer</h4>
                <div className="space-y-3 text-sm text-slate-700">
                  <p><strong>Name:</strong> {selectedEnquiry.name || "-"}</p>
                  <p><strong>Company:</strong> {selectedEnquiry.company || "-"}</p>
                  <p><strong>Phone:</strong> {selectedEnquiry.phone || "-"}</p>
                  <p><strong>Email:</strong> {selectedEnquiry.email || "-"}</p>
                  <p><strong>Service:</strong> {selectedEnquiry.serviceRequired || "-"}</p>
                  <p><strong>Submitted:</strong> {formatDate(selectedEnquiry.createdAt)}</p>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
                <h4 className="mb-4 text-sm font-bold uppercase tracking-[0.16em] text-slate-500">Message</h4>
                <p className="whitespace-pre-wrap text-sm leading-7 text-slate-700">
                  {selectedEnquiry.message || "-"}
                </p>
              </div>
            </div>

            <div className="grid gap-3 border-t border-slate-200 px-5 py-5 lg:grid-cols-[1fr,auto] lg:items-end lg:px-6">
              <div className="space-y-2">
                <label className="block text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                  Status
                </label>
                <select
                  value={selectedEnquiry.status || "New"}
                  onChange={(event) =>
                    setSelectedEnquiry((current) => current ? { ...current, status: event.target.value } : current)
                  }
                  className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm outline-none focus:border-rose-500 focus:ring-2 focus:ring-rose-100"
                >
                  {STATUS_OPTIONS.map((status) => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              </div>

              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={updateStatus}
                  className="rounded-xl bg-slate-950 px-4 py-3 text-sm font-semibold text-white transition hover:bg-slate-800"
                >
                  Save Status
                </button>
                <button
                  type="button"
                  onClick={generateQuotation}
                  className="rounded-xl bg-rose-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-rose-500"
                >
                  Generate Quotation
                </button>
                <button
                  type="button"
                  onClick={deleteEnquiry}
                  className="rounded-xl bg-white px-4 py-3 text-sm font-semibold text-rose-700 ring-1 ring-rose-200 transition hover:bg-rose-50"
                >
                  Delete
                </button>
              </div>
            </div>

            {modalNotice ? (
              <div className="border-t border-slate-200 px-5 py-4 text-sm text-slate-600 lg:px-6">{modalNotice}</div>
            ) : null}
          </div>
        </div>
      )}

      <style>{`
        .status-new {
          background: rgba(14, 165, 233, 0.12);
          border-color: rgba(14, 165, 233, 0.25);
          color: rgb(2, 132, 199);
        }
        .status-contacted {
          background: rgba(250, 204, 21, 0.15);
          border-color: rgba(250, 204, 21, 0.25);
          color: rgb(161, 98, 7);
        }
        .status-quotation-sent {
          background: rgba(244, 63, 94, 0.12);
          border-color: rgba(244, 63, 94, 0.25);
          color: rgb(190, 18, 60);
        }
        .status-closed {
          background: rgba(34, 197, 94, 0.12);
          border-color: rgba(34, 197, 94, 0.25);
          color: rgb(21, 128, 61);
        }
      `}</style>
    </div>
  );
}
